import {useQuery} from '@tanstack/react-query';
import {useMemo} from 'react';
import {fetchRest} from '../helpers/fetchRest';
import utils from '../helpers/utils';
import {TXM_DEFAULT_LOOK_BACK} from '../constants/durations';
import dateUtils from '../helpers/dateUtils';
import _get from 'lodash/get';

export function isUnifiedTransactionsFeatureEnabled(productFeatures: ProductFeatures): boolean {
  const featureStatusFw = _get(productFeatures, 'financialWellness.featureStatus', null);
  return (
    (window.REACT_APP_SHOW_UNIFIED_TRANSACTIONS && featureStatusFw === 'AVAILABLE') ||
    (window.REACT_APP_ENABLE_YODLEE_UDM &&
      (utils.isFeatureAvailable(productFeatures, 'financialMonitoringUk') ||
        utils.isFeatureAvailable(productFeatures, 'transactionMonitoring')))
  );
}

/**
 * get user transactions unified
 * @param {string} accountId - user account id to get transactions
 * @param {ProductFeatures} productFeatures - ProductFeatures
 * @param {boolean} hasInstitutions - boolean to determine if the user has institutions
 * @return {Object} returns users transactions, success and error fields
 */
/** Same query the hook uses — exported for Card Shield FM debug curl logging. */
export function buildTransactionsUnifiedListRequest(productFeatures: ProductFeatures): {
  url: string;
  transactionArgs: Record<string, string>;
} {
  const startDate = new Date(dateUtils.getStartDate(TXM_DEFAULT_LOOK_BACK)).toISOString();
  const endDate = new Date().toISOString();
  const hasTxnMonitoring = utils.isFeatureAvailable(productFeatures, 'transactionMonitoring');
  const transactionArgs: Record<string, string> = {
    startDate,
    endDate,
    sortBy: 'date',
    sortOrder: 'desc',
    count: '30',
    status: hasTxnMonitoring ? 'POSTED,PENDING' : 'POSTED',
  };
  const queryParam = new URLSearchParams(transactionArgs).toString();
  return {url: utils.getTransactionsUnifiedPath(queryParam), transactionArgs};
}

const useTransactionsUnified = (accountId: string, productFeatures: ProductFeatures, hasInstitutions: boolean) => {
  const {url} = useMemo(() => buildTransactionsUnifiedListRequest(productFeatures), [productFeatures]);

  // Keep the same gating logic as unified components:
  // - Plaid/unified UI path: controlled by REACT_APP_SHOW_UNIFIED_TRANSACTIONS + financialWellness availability
  // - Yodlee UDM migration path: controlled by REACT_APP_ENABLE_YODLEE_UDM + txm-related features
  const isUnifiedTransactionsEnabled = isUnifiedTransactionsFeatureEnabled(productFeatures);

  const query = useQuery({
    queryKey: ['transactionsUnified', accountId],
    queryFn: () =>
      fetchRest(url, {
        method: 'GET',
        headers: {Accept: 'application/json', account_id: accountId},
      }),
    enabled: !!accountId && isUnifiedTransactionsEnabled && hasInstitutions,
  });
  return {...query, isLoading: query.isPending};
};

export default useTransactionsUnified;
